import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { Edit02Icon, FloppyDiskIcon, LinkBackwardIcon } from '@hugeicons-pro/core-bulk-rounded'
import { Box, IconButton, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import { useUpdateOrderItemsAttributesMutation } from '@/app/services/orderItems'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import { useMutationErrorSnackbar } from '@/common/hooks/useMutationErrorSnackbar'

const ItemName = ({ id, isPartFromLibrary, name }) => {
    const [isEditing, setIsEditing] = useState(false)
    const [itemName, setItemName] = useState(name)
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { showMutationError } = useMutationErrorSnackbar()
    const { orderId } = useParams()

    const [updateOrderItems, { isLoading: isUpdating }] = useUpdateOrderItemsAttributesMutation()
    const organisationId = useSelector((state) => selectOrganisationId(state))

    useEffect(() => {
        setItemName(name)
    }, [name])

    const handleUpdateOrderItem = async (attributes) => {
        const orderItems = {
            quoteItemIds: [id],
            attributes,
        }
        try {
            const result = await updateOrderItems({
                organisationId,
                orderId,
                orderItems,
            }).unwrap()
            enqueueSnackbar(t('order item updated'), { variant: 'success' })
            return result
        } catch (error) {
            showMutationError(error, t('Failed to update the order item'))
        }
    }

    const handleItemNameChange = (event) => {
        const value = event.target.value
        setItemName(value)
    }

    const enableEditing = (event) => {
        event.stopPropagation()
        setIsEditing(true)
    }
    const saveEditing = async (event) => {
        event.stopPropagation()
        if (itemName !== name) {
            await handleUpdateOrderItem({ name: itemName })
        }
        setIsEditing(false)
    }

    const reset = (event) => {
        event.stopPropagation()
        setItemName(name)
        setIsEditing(false)
    }

    const onKeyDown = (event) => {
        if (event?.key === 'Enter') saveEditing(event)
    }

    const onKeyUp = (event) => {
        if (event?.key === ' ') event.preventDefault()
    }

    return (
        <Box>
            {isEditing ? (
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                    }}
                >
                    <TextField
                        key={`name-input-${id}`}
                        sx={{ marginRight: 1 }}
                        value={itemName}
                        onChange={handleItemNameChange}
                        onClick={(event) => event.stopPropagation()}
                        onKeyDown={onKeyDown}
                        onKeyUp={onKeyUp}
                    />

                    <IconButton
                        color="secondary"
                        data-testid="save-order-item-name"
                        disabled={isUpdating || !itemName}
                        onClick={saveEditing}
                    >
                        <HugeiconsIcon
                            icon={FloppyDiskIcon}
                            size={20}
                        />
                    </IconButton>
                    <IconButton
                        color="secondary"
                        data-testid="undo-order-item-name"
                        disabled={isUpdating}
                        onClick={reset}
                    >
                        <HugeiconsIcon
                            icon={LinkBackwardIcon}
                            size={20}
                        />
                    </IconButton>
                </Box>
            ) : (
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                    }}
                >
                    <Typography
                        marginRight={1}
                        variant="body1"
                    >
                        {itemName}
                    </Typography>
                    {!isPartFromLibrary ? (
                        <IconButton
                            color="secondary"
                            data-testid="edit-order-item-name"
                            onClick={enableEditing}
                        >
                            <HugeiconsIcon
                                icon={Edit02Icon}
                                size={20}
                            />
                        </IconButton>
                    ) : null}
                </Box>
            )}
        </Box>
    )
}

ItemName.propTypes = {
    id: PropTypes.string,
    isPartFromLibrary: PropTypes.bool,
    name: PropTypes.string,
}

export default ItemName
